// Repair ticket creation form — /repair/new?claim=:claimId
// Supports walk-in and from warranty claim (auto-fill).
import { useState, useEffect } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { repairApi } from "./repair-api";
import { apiGet } from "@/lib/api/client";

interface ClaimPreview {
  id: string;
  code: string;
  serialNo: string;
  productName: string;
  variantSku: string;
  customerId?: string;
  customerName?: string;
  issueDesc: string;
}

type Source = "walkin" | "claim";

export function RepairFormPage() {
  const navigate = useNavigate();
  // Read ?claim= query param
  const search = useSearch({ strict: false }) as { claim?: string };
  const claimId = search?.claim as string | undefined;

  const [source, setSource] = useState<Source>(claimId ? "claim" : "walkin");
  const [form, setForm] = useState({
    deviceDesc: "",
    accessories: "",
    issueDesc: "",
    estimatedCost: "",
    customerPhone: "",
    customerName: "",
    serialId: "",
    technicianId: "",
  });

  // Load claim if provided.
  const { data: claim } = useQuery({
    queryKey: ["warranty-claim-preview", claimId],
    queryFn: () => apiGet<ClaimPreview>(`/v1/warranty/claims/${claimId}`),
    enabled: !!claimId,
  });

  useEffect(() => {
    if (claim) {
      setSource("claim");
      setForm((f) => ({
        ...f,
        issueDesc: claim.issueDesc || f.issueDesc,
        deviceDesc: `${claim.productName} (${claim.variantSku}) SN:${claim.serialNo}`,
        customerName: claim.customerName ?? f.customerName,
      }));
    }
  }, [claim]);

  const create = useMutation({
    mutationFn: () => {
      if (source === "claim" && claimId) {
        return repairApi.createTicket({
          warrantyClaimId: claimId,
          issueDesc: form.issueDesc,
          accessories: form.accessories || undefined,
          estimatedCost: form.estimatedCost || undefined,
          technicianId: form.technicianId || undefined,
        });
      }
      return repairApi.createTicket({
        customerPhone: form.customerPhone || undefined,
        customerName: form.customerName || undefined,
        deviceDesc: form.deviceDesc,
        accessories: form.accessories || undefined,
        issueDesc: form.issueDesc,
        estimatedCost: form.estimatedCost || undefined,
        technicianId: form.technicianId || undefined,
      });
    },
    onSuccess: (ticket) => {
      navigate({ to: `/repair/${ticket.id}` as string });
    },
  });

  function validate(): string | null {
    if (!form.issueDesc.trim()) return "Vui lòng nhập mô tả lỗi";
    if (source === "walkin") {
      if (!form.deviceDesc.trim()) return "Vui lòng nhập tên thiết bị";
      if (!form.customerPhone.trim() && !form.customerName.trim()) {
        return "Vui lòng nhập số điện thoại hoặc tên khách hàng";
      }
    }
    return null;
  }

  const validationError = validate();

  return (
    <div className="mx-auto max-w-xl space-y-6">
      {/* Header */}
      <div>
        <button onClick={() => navigate({ to: "/repair" })} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-1">
          <ChevronLeft size={14} /> Danh sách sửa chữa
        </button>
        <h1 className="text-2xl font-bold">Tạo phiếu sửa chữa</h1>
      </div>

      {/* Source toggle */}
      <div className="rounded-xl border bg-card p-5 space-y-4">
        <p className="text-sm font-medium text-muted-foreground">Nguồn phiếu</p>
        <div className="flex gap-2">
          {(["walkin", "claim"] as Source[]).map((s) => (
            <button
              key={s}
              onClick={() => setSource(s)}
              className={`flex-1 rounded-md border py-2 text-sm font-medium transition-colors ${
                source === s ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-accent"
              }`}
            >
              {s === "walkin" ? "Walk-in" : "Từ phiếu bảo hành"}
            </button>
          ))}
        </div>

        {/* Claim preview */}
        {source === "claim" && claim && (
          <div className="rounded-lg bg-muted/40 p-3 text-sm space-y-1">
            <p className="font-medium">{claim.code}</p>
            <p className="text-muted-foreground">{claim.productName} · {claim.variantSku} · SN:{claim.serialNo}</p>
            {claim.customerName && <p className="text-muted-foreground">Khách: {claim.customerName}</p>}
          </div>
        )}

        {source === "claim" && !claimId && (
          <p className="text-sm text-muted-foreground">Mở phiếu này từ trang chi tiết phiếu bảo hành để tự động điền thông tin.</p>
        )}
      </div>

      {/* Walk-in customer + device */}
      {source === "walkin" && (
        <div className="rounded-xl border bg-card p-5 space-y-4">
          <p className="text-sm font-semibold">Khách hàng</p>
          <label className="block space-y-1 text-sm">
            <span className="text-muted-foreground">Số điện thoại</span>
            <input
              type="tel"
              className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
              value={form.customerPhone}
              onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))}
              placeholder="0912..."
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-muted-foreground">Tên khách hàng</span>
            <input
              type="text"
              className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none"
              value={form.customerName}
              onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
              placeholder="Nguyễn Văn A"
            />
          </label>

          <p className="text-sm font-semibold pt-2">Thiết bị</p>
          <label className="block space-y-1 text-sm">
            <span className="text-muted-foreground">Tên thiết bị <span className="text-destructive">*</span></span>
            <input
              type="text"
              className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
              value={form.deviceDesc}
              onChange={(e) => setForm((f) => ({ ...f, deviceDesc: e.target.value }))}
              placeholder="VD: iPhone 14 Pro Max 256GB, màu xanh"
            />
          </label>
        </div>
      )}

      {/* Common fields */}
      <div className="rounded-xl border bg-card p-5 space-y-4">
        <p className="text-sm font-semibold">Thông tin sửa chữa</p>

        <label className="block space-y-1 text-sm">
          <span className="text-muted-foreground">Mô tả lỗi <span className="text-destructive">*</span></span>
          <textarea
            rows={3}
            className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
            value={form.issueDesc}
            onChange={(e) => setForm((f) => ({ ...f, issueDesc: e.target.value }))}
            placeholder="Mô tả tình trạng lỗi của thiết bị..."
          />
        </label>

        <label className="block space-y-1 text-sm">
          <span className="text-muted-foreground">Phụ kiện kèm theo</span>
          <input
            type="text"
            className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none"
            value={form.accessories}
            onChange={(e) => setForm((f) => ({ ...f, accessories: e.target.value }))}
            placeholder="VD: Sạc, tai nghe, ốp lưng..."
          />
        </label>

        <label className="block space-y-1 text-sm">
          <span className="text-muted-foreground">Chi phí dự kiến (VNĐ)</span>
          <input
            type="number"
            min={0}
            className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none"
            value={form.estimatedCost}
            onChange={(e) => setForm((f) => ({ ...f, estimatedCost: e.target.value }))}
            placeholder="0"
          />
        </label>
      </div>

      {/* Errors */}
      {create.isError && (
        <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">
          Tạo phiếu thất bại. Vui lòng kiểm tra lại thông tin.
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <button onClick={() => navigate({ to: "/repair" })} className="rounded-md border px-5 py-2 text-sm">
          Huỷ
        </button>
        <button
          onClick={() => create.mutate()}
          disabled={!!validationError || create.isPending}
          title={validationError ?? undefined}
          className="rounded-md bg-primary px-5 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40"
        >
          {create.isPending ? "Đang tạo..." : "Tạo phiếu sửa chữa"}
        </button>
      </div>
    </div>
  );
}
